import { isValidEmail, isValidPhone } from '../lib/validation'

const SKIPPED_TYPES = new Set(['hidden', 'submit', 'button', 'reset', 'image'])

const STYLES = `
.rs-field{display:flex;flex-direction:column;min-width:0}
.rs-error{margin-top:4px;font-size:13px;line-height:1.4;color:#C62828}
.rs-error:empty{display:none}
[aria-invalid="true"]{border-color:#C62828 !important}
.rs-form-status{margin-top:14px;padding:12px 14px;border-radius:10px;font-size:14px;line-height:1.5}
.rs-form-status:empty{display:none}
.rs-form-status[data-state="success"]{background:#E8F5E9;color:#1B5E20}
.rs-form-status[data-state="error"]{background:#FDECEA;color:#B71C1C}
button[disabled],input[type="submit"][disabled]{opacity:.7;cursor:progress}
`

const SUCCESS_MESSAGE = 'Thank you! Your details have been sent.'
const FALLBACK_ERROR = 'Something went wrong. Please try again.'

function isCheckable(field) {
  return field.type === 'checkbox' || field.type === 'radio'
}

function labelText(field) {
  const label =
    (field.id && field.ownerDocument.querySelector(`label[for="${CSS.escape(field.id)}"]`)) ||
    field.closest('label')
  return label?.textContent
}

function fieldLabel(field) {
  const text =
    field.getAttribute('aria-label') ||
    labelText(field) ||
    field.getAttribute('placeholder') ||
    field.name ||
    'This field'
  return text.replace(/\s*\*\s*$/, '').trim()
}

function radioGroup(field) {
  const group = field.form.elements.namedItem(field.name)
  return group instanceof field.ownerDocument.defaultView.RadioNodeList ? [...group] : [field]
}

function validateField(field) {
  if (field.type === 'checkbox') {
    return field.required && !field.checked ? 'Please check this box to continue.' : ''
  }
  if (field.type === 'radio') {
    const required = radioGroup(field).some((radio) => radio.required)
    const checked = radioGroup(field).some((radio) => radio.checked)
    return required && !checked ? 'Please choose an option.' : ''
  }

  const value = field.value.trim()
  if (field.required && !value) return `${fieldLabel(field)} is required.`
  if (!value) return ''

  if (field.dataset.rsValidate === 'full-name' && value.split(/\s+/).filter(Boolean).length < 2) {
    return 'Enter your first and last name.'
  }
  if (field.type === 'email' && !isValidEmail(value)) return 'Enter a valid email address.'
  if (field.type === 'tel' && !isValidPhone(value)) return 'Enter a valid phone number.'
  if (!field.checkValidity()) return field.validationMessage
  return ''
}

function collectFields(form) {
  const fields = []
  const radioIndexByName = new Map()

  for (const field of form.elements) {
    if (!['INPUT', 'SELECT', 'TEXTAREA'].includes(field.tagName)) continue
    if (SKIPPED_TYPES.has(field.type) || field.disabled) continue

    if (field.type === 'radio') {
      if (radioIndexByName.has(field.name)) {
        fields[radioIndexByName.get(field.name)] = field
        continue
      }
      radioIndexByName.set(field.name, fields.length)
    }
    fields.push(field)
  }

  return fields
}

function attachErrorSlot(field, id) {
  const doc = field.ownerDocument
  const error = doc.createElement('div')
  error.className = 'rs-error'
  error.id = id
  error.setAttribute('aria-live', 'polite')

  if (isCheckable(field)) {
    const anchor = field.closest('label') || field
    anchor.after(error)
  } else {
    // Wrapping keeps the field and its message in the same grid/flex cell.
    const wrapper = doc.createElement('div')
    wrapper.className = 'rs-field'
    const { gridColumn } = doc.defaultView.getComputedStyle(field)
    if (gridColumn && gridColumn !== 'auto') wrapper.style.gridColumn = gridColumn
    field.before(wrapper)
    wrapper.append(field, error)
  }

  const describedBy = [field.getAttribute('aria-describedby'), id].filter(Boolean).join(' ')
  field.setAttribute('aria-describedby', describedBy)
  return error
}

export function enhanceForms(doc, { onValidSubmit } = {}) {
  const forms = [...doc.querySelectorAll('form')]
  if (forms.length === 0) return

  const style = doc.createElement('style')
  style.textContent = STYLES
  doc.head.append(style)

  let errorCount = 0

  forms.forEach((form) => {
    form.noValidate = true

    const entries = collectFields(form).map((field) => ({
      field,
      error: attachErrorSlot(field, `rs-error-${errorCount++}`),
      touched: false,
    }))

    const check = (entry) => {
      const message = validateField(entry.field)
      entry.error.textContent = message
      if (message) entry.field.setAttribute('aria-invalid', 'true')
      else entry.field.removeAttribute('aria-invalid')
      return !message
    }

    entries.forEach((entry) => {
      const { field } = entry

      if (isCheckable(field)) {
        const targets = field.type === 'radio' ? radioGroup(field) : [field]
        targets.forEach((target) =>
          target.addEventListener('change', () => {
            entry.touched = true
            check(entry)
          }),
        )
        return
      }

      field.addEventListener('blur', () => {
        entry.touched = true
        check(entry)
      })
      field.addEventListener(field.tagName === 'SELECT' ? 'change' : 'input', () => {
        if (entry.touched) check(entry)
      })
    })

    const status = doc.createElement('div')
    status.className = 'rs-form-status'
    status.setAttribute('role', 'status')
    form.append(status)

    const setStatus = (state, message) => {
      status.dataset.state = state
      status.textContent = message
    }

    const submitButton = form.querySelector(
      'button[type="submit"], button:not([type]), input[type="submit"]',
    )
    const labelProp = submitButton?.tagName === 'INPUT' ? 'value' : 'innerHTML'

    form.addEventListener('submit', async (event) => {
      event.preventDefault()
      if (form.getAttribute('aria-busy') === 'true') return

      setStatus('', '')
      entries.forEach((entry) => {
        entry.touched = true
      })

      const invalid = entries.filter((entry) => !check(entry))
      if (invalid.length > 0) {
        invalid[0].field.focus()
        return
      }
      if (!onValidSubmit) return

      const originalLabel = submitButton?.[labelProp]
      form.setAttribute('aria-busy', 'true')
      if (submitButton) {
        submitButton.disabled = true
        submitButton[labelProp] = 'Submitting...'
      }

      try {
        await onValidSubmit(form, Object.fromEntries(new FormData(form)))
        form.reset()
        entries.forEach((entry) => {
          entry.touched = false
          entry.error.textContent = ''
          entry.field.removeAttribute('aria-invalid')
        })
        setStatus('success', SUCCESS_MESSAGE)
      } catch (error) {
        setStatus('error', error.message || FALLBACK_ERROR)
      } finally {
        form.removeAttribute('aria-busy')
        if (submitButton) {
          submitButton.disabled = false
          submitButton[labelProp] = originalLabel
        }
      }
    })
  })
}
